// §11.6 — Settings. M3.9 shipped Personalization: wallpaper, accent, light/dark/system and the
// skin picker (D-041 — guests get it too). M4.6 added Storage, §7.6's readout. M10.4 added Sounds
// — §15's global mute, per-event toggles and §13's do-not-disturb. **M13.7 completes the set**:
// Keyboard, Default apps, Apps, Account and About join them, and Storage grows §7.7's Export
// everything and the guest reset. The five new pages live in `pages.tsx`; this file is the app's
// shell — the nav, the page switch, and Personalization.
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import {
  SOUND_IDS,
  type SettingsShellAccess,
  type SkinId,
  type SoundId,
  type StorageReadout,
  type ThemePreference,
} from '@hangar/shared';
import { formatBytes } from '@/assets/bytes';
import { COPY } from '@/copy';
import { AboutPage, AccountPage, AppsPage, DefaultAppsPage, KeyboardPage } from './pages';

// §11.6's pages, in its own order.
const PAGES = [
  { id: 'personalization', label: COPY.settings.pages.personalization },
  { id: 'sounds', label: COPY.settings.pages.sounds },
  { id: 'keyboard', label: COPY.settings.pages.keyboard },
  { id: 'defaultApps', label: COPY.settings.pages.defaultApps },
  { id: 'storage', label: COPY.settings.pages.storage },
  { id: 'apps', label: COPY.settings.pages.apps },
  { id: 'account', label: COPY.settings.pages.account },
  { id: 'about', label: COPY.settings.pages.about },
] as const;

type PageId = (typeof PAGES)[number]['id'];

/** §15 — what each `SoundId` is *for*, said in the user's words rather than the code's. */
const SOUND_LABELS: Record<SoundId, string> = {
  notify: 'Notification',
  error: 'Error',
  snap: 'Window snap',
  trash: 'Move to Recycle Bin',
  screenshot: 'Screenshot',
  deviceConnect: 'Device connected',
  startup: 'Startup',
};

// Moved to `assets/` at M13.6 so the Recycle Bin's usage line reads in the same units (§6.5).
export { formatBytes } from '@/assets/bytes';

/**
 * §15 — "Global mute and per-event toggles in Settings."
 *
 * Each row has a Play button beside its toggle, and that is not decoration: a list of seven event
 * names is unusable without hearing what they are, and the click is also the user gesture the
 * autoplay policy requires before any sound can be made at all (D-143).
 */
function SoundsPage({ access }: { access: SettingsShellAccess }) {
  const muted = access.get('sound.muted') === true;
  const disabled = (access.get('sound.disabled') as SoundId[] | undefined) ?? [];
  const dnd = access.get('notify.doNotDisturb') === true;

  const toggle = (id: SoundId): void => {
    const next = disabled.includes(id)
      ? disabled.filter((entry) => entry !== id)
      : [...disabled, id];
    access.set('sound.disabled', next);
  };

  return (
    <>
      <section className="settings-group" aria-labelledby="settings-sound-global">
        <h3 className="settings-group-title" id="settings-sound-global">
          System sounds
        </h3>
        <label className="settings-toggle">
          <input
            type="checkbox"
            data-testid="settings-sound-muted"
            checked={muted}
            onChange={(event) => access.set('sound.muted', event.target.checked)}
          />
          Mute all system sounds
        </label>
        {/* §13 — do-not-disturb lives here because it is the other thing that makes the desktop
            quiet, and a user looking for quiet looks in one place. */}
        <label className="settings-toggle">
          <input
            type="checkbox"
            data-testid="settings-do-not-disturb"
            checked={dnd}
            onChange={(event) => access.set('notify.doNotDisturb', event.target.checked)}
          />
          Do not disturb — no notification toasts
        </label>
      </section>

      <section className="settings-group" aria-labelledby="settings-sound-events">
        <h3 className="settings-group-title" id="settings-sound-events">
          Events
        </h3>
        <ul className="settings-sound-list" data-testid="settings-sound-events">
          {SOUND_IDS.map((id) => (
            <li key={id} className="settings-sound-row">
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  data-testid={`settings-sound-${id}`}
                  checked={!disabled.includes(id)}
                  disabled={muted}
                  onChange={() => toggle(id)}
                />
                {SOUND_LABELS[id]}
              </label>
              <button
                type="button"
                className="settings-sound-play"
                data-testid={`settings-sound-play-${id}`}
                onClick={() => access.playSound(id)}
              >
                Play
              </button>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

function StoragePage({ access }: { access: SettingsShellAccess }) {
  const [readout, setReadout] = useState<StorageReadout | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void access.storage().then((next) => {
      if (!cancelled) setReadout(next);
    });
    return () => {
      cancelled = true;
    };
  }, [access]);

  if (!readout) return <p data-testid="settings-storage-loading">Measuring…</p>;

  const percent = Math.round(readout.ratio * 1000) / 10;
  return (
    <>
      <section className="settings-group" aria-labelledby="settings-storage-usage">
        <h3 className="settings-group-title" id="settings-storage-usage">
          This browser
        </h3>
        <p className="settings-readout" data-testid="settings-storage-usage">
          {formatBytes(readout.usageBytes)} of {formatBytes(readout.quotaBytes)} used
          {readout.quotaBytes > 0 ? ` (${percent}%)` : ''}
        </p>
        <div
          className="settings-meter"
          role="meter"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Storage used"
          data-over={String(readout.over)}
          data-testid="settings-storage-meter"
        >
          <div className="settings-meter-fill" style={{ inlineSize: `${Math.min(100, percent)}%` }} />
        </div>
      </section>

      <section className="settings-group" aria-labelledby="settings-storage-trash">
        <h3 className="settings-group-title" id="settings-storage-trash">
          Recycle Bin
        </h3>
        <p className="settings-readout" data-testid="settings-storage-trash">
          {readout.trashItems === 1 ? '1 item' : `${readout.trashItems} items`},{' '}
          {formatBytes(readout.trashBytes)}
        </p>
      </section>

      <section className="settings-group" aria-labelledby="settings-storage-export">
        <h3 className="settings-group-title" id="settings-storage-export">
          {COPY.settings.exportEverything}
        </h3>
        <p className="settings-hint">{COPY.settings.exportHint}</p>
        {/* §7.7 — "Non-negotiable — it exists in both modes." One button, both modes. */}
        <button
          type="button"
          data-testid="settings-export-everything"
          onClick={() => void access.exportEverything()}
        >
          {COPY.settings.exportEverything}
        </button>
      </section>

      <section className="settings-group" aria-labelledby="settings-storage-reset">
        <h3 className="settings-group-title" id="settings-storage-reset">
          {COPY.settings.resetSession}
        </h3>
        <p className="settings-hint">{COPY.settings.resetHint}</p>
        <button
          type="button"
          data-testid="settings-reset-session"
          onClick={() => {
            // §19.4's confirm is raised inside the capability, so this cannot skip it.
            void access.resetGuestSession().then((done) => {
              if (done) setDone(COPY.settings.resetDone);
            });
          }}
        >
          {COPY.settings.resetSession}
        </button>
        {done ? (
          <p className="settings-readout" role="status" data-testid="settings-reset-done">
            {done}
          </p>
        ) : null}
      </section>
    </>
  );
}

/** D-044's amber plus a spread that reads well against both themes. */
const ACCENTS = [
  { value: '', label: 'Skin default' },
  { value: '#E8963C', label: 'Amber' },
  { value: '#4F9DDE', label: 'Sky' },
  { value: '#58A46A', label: 'Fern' },
  { value: '#C56BC0', label: 'Orchid' },
  { value: '#D9534F', label: 'Coral' },
];

const THEMES: Array<{ value: ThemePreference; label: string }> = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

export function Settings({ access }: { access: SettingsShellAccess }) {
  // Settings changed elsewhere — a skin picker in another window, a chord — must show here.
  const subscribe = useCallback((listener: () => void) => access.subscribe(listener), [access]);
  const snapshot = useCallback(
    () =>
      JSON.stringify([
        access.get('skin'),
        access.get('theme'),
        access.get('accent'),
        access.get('wallpaper'),
        access.get('sound.muted'),
        access.get('sound.disabled'),
        access.get('notify.doNotDisturb'),
      ]),
    [access],
  );
  useSyncExternalStore(subscribe, snapshot, snapshot);
  const [page, setPage] = useState<PageId>('personalization');

  const skin = access.get('skin') as SkinId;
  const theme = access.get('theme') as ThemePreference;
  const accent = access.get('accent') as string;
  const wallpaper = access.get('wallpaper') as string;

  return (
    <div className="settings" data-testid="settings">
      <nav className="settings-nav" aria-label="Settings pages">
        {PAGES.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className="settings-nav-item"
            data-testid={`settings-page-${entry.id}`}
            data-active={String(page === entry.id)}
            onClick={() => setPage(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </nav>

      <div className="settings-page">
        <h2 className="settings-heading">
          {PAGES.find((entry) => entry.id === page)?.label ?? 'Personalization'}
        </h2>

        {page === 'storage' ? <StoragePage access={access} /> : null}
        {page === 'sounds' ? <SoundsPage access={access} /> : null}
        {page === 'keyboard' ? <KeyboardPage access={access} /> : null}
        {page === 'defaultApps' ? <DefaultAppsPage access={access} /> : null}
        {page === 'apps' ? <AppsPage access={access} /> : null}
        {page === 'account' ? <AccountPage access={access} /> : null}
        {page === 'about' ? <AboutPage access={access} /> : null}

        <section className="settings-group" hidden={page !== 'personalization'} aria-labelledby="settings-skin">
          <h3 className="settings-group-title" id="settings-skin">
            Skin
          </h3>
          <div className="settings-choices" data-testid="settings-skins">
            {access.skins().map((choice) => (
              <button
                key={choice.id}
                type="button"
                className="settings-choice"
                data-testid={`settings-skin-${choice.id}`}
                data-active={String(skin === choice.id)}
                onClick={() => access.set('skin', choice.id)}
              >
                {choice.label}
              </button>
            ))}
          </div>
        </section>

        <section className="settings-group" hidden={page !== 'personalization'} aria-labelledby="settings-theme">
          <h3 className="settings-group-title" id="settings-theme">
            Appearance
          </h3>
          <div className="settings-choices" data-testid="settings-themes">
            {THEMES.map((choice) => (
              <button
                key={choice.value}
                type="button"
                className="settings-choice"
                data-testid={`settings-theme-${choice.value}`}
                data-active={String(theme === choice.value)}
                onClick={() => access.set('theme', choice.value)}
              >
                {choice.label}
              </button>
            ))}
          </div>
        </section>

        <section className="settings-group" hidden={page !== 'personalization'} aria-labelledby="settings-accent">
          <h3 className="settings-group-title" id="settings-accent">
            Accent colour
          </h3>
          <div className="settings-choices" data-testid="settings-accents">
            {ACCENTS.map((choice) => (
              <button
                key={choice.value || 'default'}
                type="button"
                className="settings-swatch"
                data-testid={`settings-accent-${choice.value || 'default'}`}
                data-active={String(accent === choice.value)}
                title={choice.label}
                aria-label={choice.label}
                style={choice.value ? { background: choice.value } : undefined}
                onClick={() => access.set('accent', choice.value)}
              />
            ))}
          </div>
        </section>

        <section className="settings-group" hidden={page !== 'personalization'} aria-labelledby="settings-wallpaper">
          <h3 className="settings-group-title" id="settings-wallpaper">
            Wallpaper
          </h3>
          <div className="settings-wallpapers" data-testid="settings-wallpapers">
            {access.wallpapers().map((choice) => (
              <button
                key={choice.id}
                type="button"
                className="settings-wallpaper"
                data-testid={`settings-wallpaper-${choice.id}`}
                data-active={String(wallpaper === choice.id)}
                style={{ background: choice.css }}
                aria-label={choice.label}
                onClick={() => access.set('wallpaper', choice.id)}
              >
                <span className="settings-wallpaper-label">{choice.label}</span>
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
